import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  WorkingHoursListLoad,
  WorkingHoursItemLoad,
  WorkingHoursInitialState,
  WorkingHoursItemCreate,
  WorkingHoursItemUpdate,
  WorkingHoursItemDelete,
  WorkingHoursItemInitialState,
} from './working-hours.actions';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { WorkingHoursService } from './working-hours.service';
import { WorkingHoursBrief, WorkingHoursModel, WorkingHoursResponseModel } from './models';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface WorkingHoursStateModel {
  list: WorkingHoursBrief[];
  item: WorkingHoursModel;
  compartmentTimezone: string;
  loaded: boolean;
}

const initialState: WorkingHoursStateModel = {
  list: [],
  item: null,
  compartmentTimezone: null,
  loaded: false,
};

@State<WorkingHoursStateModel>({
  name: 'workingHours',
  defaults: initialState,
})
@Injectable()
export class WorkingHoursState {
  constructor(
    private workingHoursService: WorkingHoursService,
    private notificationService: NotificationService,
    private navigateUserService: NavigateUserService,
  ) {}

  @Selector()
  static getCalendarList(state: WorkingHoursStateModel): WorkingHoursBrief[] {
    return state.list;
  }

  @Selector()
  static getUsedDaysList(state: WorkingHoursStateModel): number[] {
    return state.list
      .map((workingHoursItem: WorkingHoursBrief) => workingHoursItem.validFrom)
      .filter((timestamp: number) => timestamp !== state.item?.validFrom);
  }

  @Selector()
  static getSelectedWorkingHours(state: WorkingHoursStateModel): WorkingHoursModel {
    return state.item;
  }

  @Selector()
  static getCompartmentTimezone(state: WorkingHoursStateModel): string {
    return state.compartmentTimezone;
  }

  @Selector()
  static getLoaded(state: WorkingHoursStateModel): boolean {
    return state.loaded;
  }

  @Action(WorkingHoursListLoad)
  getWorkingHoursList(
    ctx: StateContext<WorkingHoursStateModel>,
    { payload: { compartmentId, dockId } }: WorkingHoursListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.workingHoursService.getWorkingHoursList(compartmentId, dockId).pipe(
      take(1),
      map((data: WorkingHoursResponseModel) => {
        ctx.patchState({
          list: data.calendars,
          compartmentTimezone: data.timezone,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(WorkingHoursItemLoad)
  getWorkingHoursItem(
    ctx: StateContext<WorkingHoursStateModel>,
    { payload: { compartmentId, dockId, workingHoursId } }: WorkingHoursItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.workingHoursService.getWorkingHoursItem(workingHoursId, compartmentId, dockId).pipe(
      take(1),
      map((item: WorkingHoursModel) => {
        ctx.patchState({ item, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(WorkingHoursItemCreate)
  createWorkingHoursItem(
    ctx: StateContext<WorkingHoursStateModel>,
    { payload: { hubId, compartmentId, dockId, data } }: WorkingHoursItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.workingHoursService.updateWorkingHours(data, compartmentId, dockId).pipe(
      take(1),
      map((item: WorkingHoursModel) => {
        ctx.patchState({ item, loaded: true });
        this.navigateUser(hubId, compartmentId, dockId, item.id.toString());
        this.notificationService.success('api.workingHours.success.create');
        this.updateList(ctx, compartmentId, dockId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(WorkingHoursItemUpdate)
  updateWorkingHoursItem(
    ctx: StateContext<WorkingHoursStateModel>,
    { payload: { data, compartmentId, dockId } }: WorkingHoursItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.workingHoursService.updateWorkingHours(data, compartmentId, dockId).pipe(
      take(1),
      map((item: WorkingHoursModel) => {
        this.notificationService.success('api.workingHours.success.update');
        this.updateList(ctx, compartmentId, dockId);
        ctx.patchState({ item, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(WorkingHoursItemDelete)
  deleteWorkingHoursItem(
    ctx: StateContext<WorkingHoursStateModel>,
    { payload: { hubId, compartmentId, dockId, workingHoursIds, dialog } }: WorkingHoursItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.workingHoursService.deleteWorkingHours(workingHoursIds, compartmentId, dockId).pipe(
      take(1),
      map(() => {
        dialog.close();
        this.notificationService.success('api.workingHours.success.delete');
        this.navigateUser(hubId, compartmentId, dockId, 'create');
        this.updateList(ctx, compartmentId, dockId);
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(WorkingHoursItemInitialState)
  itemSetInitialState(ctx: StateContext<WorkingHoursStateModel>): void {
    ctx.patchState({ item: null });
  }

  @Action(WorkingHoursInitialState)
  setInitialState(ctx: StateContext<WorkingHoursStateModel>): void {
    ctx.setState(initialState);
  }

  private navigateUser(hubId: number, compartmentId: number, dockId: number, path: string): void {
    let baseUrl = `dashboard/administration/hubs/${hubId}/compartments/${compartmentId}`;
    if (dockId) {
      baseUrl += `/docks/${dockId}`;
    }

    this.navigateUserService.navigateUserToAbsolutePath(`${baseUrl}/working-hours/${path}`);
  }

  private updateList(ctx: StateContext<WorkingHoursStateModel>, compartmentId: number, dockId: number): void {
    ctx.dispatch(new WorkingHoursListLoad({ compartmentId, dockId }));
  }
}
